import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/data';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminUsers.css';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    phone: '',
    role: '',
    is_active: true,
    is_verified: false
  });
  const [showPassportModal, setShowPassportModal] = useState(false);
  const [editingPassport, setEditingPassport] = useState(null);
  const [passportForm, setPassportForm] = useState({
    series: '',
    number: '',
    issued_by: '',
    issue_date: '',
    department_code: '',
    gender: '',
    birth_date: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    phone: '',
    password: '',
    firstName: '',
    lastName: '',
    middleName: ''
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchUsers();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate, currentPage]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm })
      });

      const response = await axios.get(`${API_URL}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data.users || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Ошибка загрузки пользователей');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };


  const handleViewDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedUser(response.data);
      setShowUserDetails(true);
      
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Ошибка загрузки деталей пользователя');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      middle_name: user.middle_name || '',
      phone: user.phone || '',
      role: user.role || '',
      is_active: user.is_active,
      is_verified: user.is_verified || false
    });
    setShowEditModal(true);
    
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowEditModal(false);
      setEditingUser(null);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Ошибка обновления пользователя');
    }
  };

  const handleEditPassport = (passport) => {
    setEditingPassport(passport);
    if (passport) {
      setPassportForm({
        series: passport.series || '',
        number: passport.number || '',
        issued_by: passport.issued_by || '',
        issue_date: passport.issue_date || '',
        department_code: passport.department_code || '',
        gender: passport.gender || '',
        birth_date: passport.birth_date || ''
      });
    } else {
      setPassportForm({
        series: '',
        number: '',
        issued_by: '',
        issue_date: '',
        department_code: '',
        gender: '',
        birth_date: ''
      });
    }
    setShowPassportModal(true);
  };

  const handlePassportFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPassportForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSavePassport = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/users/${selectedUser.user.id}/passport`, passportForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowPassportModal(false);
      setEditingPassport(null);
      // await fetchUserDetails(selectedUser.id);
    } catch (error) {
      console.error('Error updating passport:', error);
      setError('Ошибка обновления паспортных данных');
    }
  };

  const handleCreateUser = () => {
    setCreateForm({
      email: '',
      phone: '',
      password: '',
      firstName: '',
      lastName: '',
      middleName: ''
    });
    setShowCreateModal(true);
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveCreateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/users`, createForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateModal(false);
      setCreateForm({
        email: '',
        phone: '',
        password: '',
        firstName: '',
        lastName: '',
        middleName: ''
      });
      await fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      setError('Ошибка создания пользователя: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleChangePassword = (user) => {
    setEditingUser(user);
    setPasswordForm({
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    
    // Валидация паролей
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      setError('Пароль должен содержать минимум 8 символов');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/users/${editingUser.id}/password`, {
        newPassword: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowPasswordModal(false);
      setPasswordForm({
        newPassword: '',
        confirmPassword: ''
      });
      setEditingUser(null);
      setError(null);
    } catch (error) {
      console.error('Error changing password:', error);
      setError('Ошибка смены пароля: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`ВНИМАНИЕ! Вы собираетесь удалить пользователя "${userName}". Это действие нельзя отменить и удалит ВСЕ данные пользователя (счета, операции, документы). Вы уверены, что хотите продолжить?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchUsers();
      setError(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Ошибка удаления пользователя: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency = 'RUB') => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <div className="admin-users">
        <div className="admin-header">
          <div className="admin-header__logo">
            <img src="/assets/img/logo.svg" alt="Pro Investor" />
          </div>
          <div className="admin-header__title">Управление пользователями</div>
          <div className="admin-header__actions">
            <button className="admin-header__back" onClick={() => navigate('/admin')}>
              <span className="--icon-arrow-left"></span>
              Назад
            </button>
            <button className="admin-header__logout" onClick={() => logoutAdmin()}>
              <span className="--icon-log-out"></span>
              Выйти
            </button>
          </div>
        </div>
        <div className="admin-loading">
          <div className="admin-loading__spinner"></div>
          <p>Загрузка пользователей...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Управление пользователями</div>
        <div className="admin-header__actions">
          <button className="admin-header__back" onClick={() => navigate('/admin')}>
            <span className="--icon-arrow-left"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={() => logoutAdmin()}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        <div className="admin-content__header">
          <h1 className="admin-title">Пользователи</h1>
          <div className="admin-content__actions">
            <button className="admin-btn admin-btn--primary" onClick={handleCreateUser}>
              <span className="--icon-plus"></span>
              Создать пользователя
            </button>
            <button className="admin-refresh-btn" onClick={fetchUsers}>
              <span className="--icon-arrow"></span>
              Обновить
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="admin-filters">
          <form onSubmit={handleSearch} className="admin-search">
            <input
              type="text"
              placeholder="Поиск по имени, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-search__input"
            />
            <button type="submit" className="admin-search__btn">
              <span className="--icon-search"></span>
            </button>
          </form>

        </div>

        {error && (
          <div className="admin-error">
            <p>{error}</p>
            <button onClick={fetchUsers} className="admin-btn admin-btn--primary">
              Попробовать снова
            </button>
          </div>
        )}

        {/* Users Table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Имя</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Дата регистрации</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.first_name} {user.last_name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className="admin-role">
                      {user.role || 'Не указана'}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-status admin-status--${user.status}`}>
                      {user.is_active ? 'Активный' : 'Заблокирован'}
                    </span>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                         <td>
                           <div className="admin-actions">
                             <button
                               className="admin-btn admin-btn--sm admin-btn--info"
                               onClick={() => handleViewDetails(user.id)}
                             >
                               Детали
                             </button>
                             <button
                               className="admin-btn admin-btn--sm admin-btn--primary"
                               onClick={() => handleEditUser(user)}
                             >
                               Редактировать
                             </button>
                             <button
                               className="admin-btn admin-btn--sm admin-btn--secondary"
                               onClick={() => handleChangePassword(user)}
                             >
                               Сменить пароль
                             </button>
                             <button
                               className="admin-btn admin-btn--sm admin-btn--danger"
                               onClick={() => handleDeleteUser(user.id, `${user.first_name} ${user.last_name}`)}
                             >
                               Удалить
                             </button>
                           </div>
                         </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination__btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Назад
            </button>
            <span className="admin-pagination__info">
              Страница {currentPage} из {totalPages}
            </span>
            <button
              className="admin-pagination__btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Вперед
            </button>
          </div>
        )}

        {/* User Details Modal */}
        {showUserDetails && selectedUser && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={() => setShowUserDetails(false)}></div>
            <div className="admin-modal__content">
              <div className="admin-modal__header">
                <h2>Детали пользователя</h2>
                <button
                  className="admin-modal__close"
                  onClick={() => setShowUserDetails(false)}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <div className="admin-user-details">
                  <div className="admin-user-details__section">
                    <h3>Основная информация</h3>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">ID:</span>
                      <span className="admin-detail-value">{selectedUser.user.id}</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Имя:</span>
                      <span className="admin-detail-value">
                        {selectedUser.user.first_name} {selectedUser.user.last_name}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Email:</span>
                      <span className="admin-detail-value">{selectedUser.user.email}</span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Роль:</span>
                      <span className="admin-detail-value">
                        {selectedUser.user.role || 'Не указана'}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Статус:</span>
                      <span className={`admin-status admin-status--${selectedUser.user.status}`}>
                        {selectedUser.user.is_active ? 'Активный' : 'Заблокирован'}
                      </span>
                    </div>
                    <div className="admin-detail-row">
                      <span className="admin-detail-label">Дата регистрации:</span>
                      <span className="admin-detail-value">{formatDate(selectedUser.user.created_at)}</span>
                    </div>
                  </div>

                  <div className="admin-user-details__section">
                    <h3>Банковские счета ({selectedUser.accounts?.length || 0})</h3>
                    {selectedUser.accounts?.length > 0 ? (
                      <div className="admin-accounts-list">
                        {selectedUser.accounts.map((account) => (
                          <div key={account.id} className="admin-account-item">
                            <div className="admin-account-item__info">
                              <span className="admin-account-item__number">{account.number}</span>
                              <span className="admin-account-item__bank">{account.bank}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="admin-no-data">Нет банковских счетов</p>
                    )}
                  </div>

                  <div className="admin-user-details__section">
                    <h3>Торговые счета ({selectedUser.tradingAccounts?.length || 0})</h3>
                    {selectedUser.tradingAccounts?.length > 0 ? (
                      <div className="admin-accounts-list">
                        {selectedUser.tradingAccounts.map((account) => (
                          <div key={account.id} className="admin-account-item">
                            <div className="admin-account-item__info">
                              <span className="admin-account-item__number">{account.account_number}</span>
                              <span className="admin-account-item__currency">{account.currency}</span>
                            </div>
                            <div className="admin-account-item__balance">
                              {formatCurrency(account.balance, account.currency)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="admin-no-data">Нет торговых счетов</p>
                    )}
                  </div>

                  <div className="admin-user-details__section">
                    <h3>Паспортные данные</h3>
                    {selectedUser.passport ? (
                      <div className="admin-passport-details">
                        <div className="admin-detail-row">
                          <span className="admin-detail-label">Серия:</span>
                          <span className="admin-detail-value">{selectedUser.passport.series || 'Не указано'}</span>
                        </div>
                        <div className="admin-detail-row">
                          <span className="admin-detail-label">Номер:</span>
                          <span className="admin-detail-value">{selectedUser.passport.number || 'Не указано'}</span>
                        </div>
                        <div className="admin-detail-row">
                          <span className="admin-detail-label">Кем выдан:</span>
                          <span className="admin-detail-value">{selectedUser.passport.issued_by || 'Не указано'}</span>
                        </div>
                        <div className="admin-detail-row">
                          <span className="admin-detail-label">Дата выдачи:</span>
                          <span className="admin-detail-value">
                            {selectedUser.passport.issue_date ? formatDate(selectedUser.passport.issue_date) : 'Не указано'}
                          </span>
                        </div>
                        <div className="admin-detail-row">
                          <span className="admin-detail-label">Код подразделения:</span>
                          <span className="admin-detail-value">{selectedUser.passport.department_code || 'Не указано'}</span>
                        </div>
                        <div className="admin-detail-row">
                          <span className="admin-detail-label">Пол:</span>
                          <span className="admin-detail-value">{selectedUser.passport.gender || 'Не указано'}</span>
                        </div>
                        <div className="admin-detail-row">
                          <span className="admin-detail-label">Дата рождения:</span>
                          <span className="admin-detail-value">
                            {selectedUser.passport.birth_date ? formatDate(selectedUser.passport.birth_date) : 'Не указано'}
                          </span>
                        </div>
                        <div className="admin-detail-row">
                          <button
                            className="admin-btn admin-btn--sm admin-btn--primary"
                            onClick={() => handleEditPassport(selectedUser.passport)}
                          >
                            Редактировать паспортные данные
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <p className="admin-no-data">Паспортные данные не заполнены</p>
                        <button
                          className="admin-btn admin-btn--sm admin-btn--primary"
                          onClick={() => handleEditPassport(null)}
                        >
                          Добавить паспортные данные
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="admin-user-details__section">
                    <h3>Последние операции ({selectedUser.recentOperations?.length || 0})</h3>
                    {selectedUser.recentOperations?.length > 0 ? (
                      <div className="admin-operations-list">
                        {selectedUser.recentOperations.map((operation) => (
                          <div key={operation.id} className="admin-operation-item">
                            <div className="admin-operation-item__type">
                              {operation.operation_type === 'deposit' ? 'Пополнение' : 'Вывод'}
                            </div>
                            <div className="admin-operation-item__amount">
                              {formatCurrency(operation.amount, operation.currency)}
                            </div>
                            <div className="admin-operation-item__status">
                              <span className={`admin-status admin-status--${operation.status}`}>
                                {operation.status}
                              </span>
                            </div>
                            <div className="admin-operation-item__date">
                              {formatDate(operation.created_at)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="admin-no-data">Нет операций</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditModal && editingUser && (
          <div className="admin-modal">
            <div className="admin-modal__overlay" onClick={() => setShowEditModal(false)}></div>
            <div className="admin-modal__content">
              <div className="admin-modal__header">
                <h2>Редактировать пользователя</h2>
                <button
                  className="admin-modal__close"
                  onClick={() => setShowEditModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="admin-modal__body">
                <form onSubmit={handleSaveUser} className="admin-edit-form">
                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={editForm.email}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="phone">Телефон</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={editForm.phone}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                      />
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="first_name">Имя *</label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={editForm.first_name}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="last_name">Фамилия *</label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={editForm.last_name}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                        required
                      />
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label htmlFor="middle_name">Отчество</label>
                      <input
                        type="text"
                        id="middle_name"
                        name="middle_name"
                        value={editForm.middle_name}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                      />
                    </div>
                    <div className="admin-edit-form__field">
                      <label htmlFor="role">Роль</label>
                      <select
                        id="role"
                        name="role"
                        value={editForm.role.toLowerCase() || ''}
                        onChange={handleEditFormChange}
                        className="admin-edit-form__input"
                      >
                        <option value="">Выберите роль</option>
                        <option value="инвестор">Инвестор</option>
                        <option value="неквалифицированный инвестор">Неквалифицированный инвестор</option>
                        <option value="квалифицированный инвестор">Квалифицированный инвестор</option>
                        <option value="квалифицированный управляющий">Квалифицированный управляющий</option>
                      </select>
                    </div>
                  </div>

                  <div className="admin-edit-form__row">
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__checkbox">
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={editForm.is_active}
                          onChange={handleEditFormChange}
                        />
                        <span>Активный пользователь</span>
                      </label>
                    </div>
                    <div className="admin-edit-form__field">
                      <label className="admin-edit-form__checkbox">
                        <input
                          type="checkbox"
                          name="is_verified"
                          checked={editForm.is_verified}
                          onChange={handleEditFormChange}
                        />
                        <span>Верифицированный пользователь</span>
                      </label>
                    </div>
                  </div>

                  <div className="admin-edit-form__actions">
                    <button
                      type="button"
                      className="admin-btn admin-btn--secondary"
                      onClick={() => setShowEditModal(false)}
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="admin-btn admin-btn--primary"
                    >
                      Сохранить
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

      {/* Edit Passport Modal */}
      {showPassportModal && selectedUser && (
        <div className="admin-modal">
          <div className="admin-modal__overlay" onClick={() => setShowPassportModal(false)}></div>
          <div className="admin-modal__content">
            <div className="admin-modal__header">
              <h2>{editingPassport ? 'Редактировать паспортные данные' : 'Добавить паспортные данные'}</h2>
              <button
                className="admin-modal__close"
                onClick={() => setShowPassportModal(false)}
              >
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <form onSubmit={handleSavePassport} className="admin-edit-form">
                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="series">Серия</label>
                    <input
                      type="text"
                      id="series"
                      name="series"
                      value={passportForm.series}
                      onChange={handlePassportFormChange}
                      className="admin-edit-form__input"
                      maxLength="10"
                    />
                  </div>
                  <div className="admin-edit-form__field">
                    <label htmlFor="number">Номер</label>
                    <input
                      type="text"
                      id="number"
                      name="number"
                      value={passportForm.number}
                      onChange={handlePassportFormChange}
                      className="admin-edit-form__input"
                      maxLength="20"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="issued_by">Кем выдан</label>
                    <input
                      type="text"
                      id="issued_by"
                      name="issued_by"
                      value={passportForm.issued_by}
                      onChange={handlePassportFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                  <div className="admin-edit-form__field">
                    <label htmlFor="department_code">Код подразделения</label>
                    <input
                      type="text"
                      id="department_code"
                      name="department_code"
                      value={passportForm.department_code}
                      onChange={handlePassportFormChange}
                      className="admin-edit-form__input"
                      maxLength="10"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="issue_date">Дата выдачи</label>
                    <input
                      type="date"
                      id="issue_date"
                      name="issue_date"
                      value={passportForm.issue_date}
                      onChange={handlePassportFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                  <div className="admin-edit-form__field">
                    <label htmlFor="birth_date">Дата рождения</label>
                    <input
                      type="date"
                      id="birth_date"
                      name="birth_date"
                      value={passportForm.birth_date}
                      onChange={handlePassportFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="gender">Пол</label>
                    <select
                      id="gender"
                      name="gender"
                      value={passportForm.gender}
                      onChange={handlePassportFormChange}
                      className="admin-edit-form__input"
                    >
                      <option value="">Выберите пол</option>
                      <option value="мужской">Мужской</option>
                      <option value="женский">Женский</option>
                    </select>
                  </div>
                </div>

                <div className="admin-edit-form__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--secondary"
                    onClick={() => setShowPassportModal(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="admin-btn admin-btn--primary"
                  >
                    {editingPassport ? 'Обновить' : 'Добавить'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="admin-modal">
          <div className="admin-modal__overlay" onClick={() => setShowCreateModal(false)}></div>
          <div className="admin-modal__content">
            <div className="admin-modal__header">
              <h2>Создать пользователя</h2>
              <button
                className="admin-modal__close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <form onSubmit={handleSaveCreateUser} className="admin-edit-form">
                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="create-email">Email *</label>
                    <input
                      type="email"
                      id="create-email"
                      name="email"
                      value={createForm.email}
                      onChange={handleCreateFormChange}
                      className="admin-edit-form__input"
                      required
                    />
                  </div>
                  <div className="admin-edit-form__field">
                    <label htmlFor="create-phone">Телефон</label>
                    <input
                      type="tel"
                      id="create-phone"
                      name="phone"
                      value={createForm.phone}
                      onChange={handleCreateFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="create-password">Пароль *</label>
                    <input
                      type="password"
                      id="create-password"
                      name="password"
                      value={createForm.password}
                      onChange={handleCreateFormChange}
                      className="admin-edit-form__input"
                      required
                      minLength="8"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="create-firstName">Имя</label>
                    <input
                      type="text"
                      id="create-firstName"
                      name="firstName"
                      value={createForm.firstName}
                      onChange={handleCreateFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                  <div className="admin-edit-form__field">
                    <label htmlFor="create-lastName">Фамилия</label>
                    <input
                      type="text"
                      id="create-lastName"
                      name="lastName"
                      value={createForm.lastName}
                      onChange={handleCreateFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="create-middleName">Отчество</label>
                    <input
                      type="text"
                      id="create-middleName"
                      name="middleName"
                      value={createForm.middleName}
                      onChange={handleCreateFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="admin-btn admin-btn--primary"
                  >
                    Создать пользователя
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && editingUser && (
        <div className="admin-modal">
          <div className="admin-modal__overlay" onClick={() => setShowPasswordModal(false)}></div>
          <div className="admin-modal__content">
            <div className="admin-modal__header">
              <h2>Сменить пароль</h2>
              <button
                className="admin-modal__close"
                onClick={() => setShowPasswordModal(false)}
              >
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <div className="admin-password-info">
                <p>Пользователь: <strong>{editingUser.first_name} {editingUser.last_name}</strong></p>
                <p>Email: <strong>{editingUser.email}</strong></p>
              </div>
              <form onSubmit={handleSavePassword} className="admin-edit-form">
                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="newPassword">Новый пароль *</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordFormChange}
                      className="admin-edit-form__input"
                      required
                      minLength="8"
                      placeholder="Минимум 8 символов"
                    />
                  </div>
                </div>
                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="confirmPassword">Подтвердите пароль *</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordFormChange}
                      className="admin-edit-form__input"
                      required
                      minLength="8"
                      placeholder="Повторите новый пароль"
                    />
                  </div>
                </div>
                <div className="admin-edit-form__actions">
                  <button
                    type="button"
                    className="admin-btn admin-btn--secondary"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="admin-btn admin-btn--primary"
                  >
                    Сменить пароль
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AdminUsers;